import { lazy, useState } from 'react';

import { PostHogErrorBoundary, useFeatureFlagEnabled } from 'posthog-js/react';
import {
  createBrowserRouter,
  createMemoryRouter,
  createRoutesFromElements,
  Outlet,
  Route
} from 'react-router';

import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/constants/constants';
import PersonalizationVariantRenderer from '@/features/ComponentVariants/PersonalizationVariantRenderer';
import DataHubExport from '@/features/DataHub/modules/Export';
import { FACTSHEET_PARAM_NAME } from '@/features/Factsheet';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { withSuspense } from '@/utils/withSuspense';

import CookieBanner from './components/CookieBanner';
import GlobalModal from './components/GlobalModal';
import { MAINTENANCE_MODE_FLAG } from './constants/featureFlags';
import { WidgetAnimationProvider } from './features/Dashboard/context/WidgetAnimation/provider';
import Listeners from './features/Listeners';
import { TrackingLister } from './features/Tracking';
import { IdentifyPostHogProvider } from './features/Tracking/context/PostHogContext';

// Lazy loaded components
const MaintenanceMode = lazy(() =>
  import('@/features/MaintenanceMode').then(module => ({ default: module.MaintenanceMode }))
);

const Catalog = lazy(() => import('@/pages/Catalog'));
const ErrorPage = lazy(() => import('@/pages/ErrorPage'));
const Expired = lazy(() => import('@/pages/Expired'));
const Home = lazy(() => import('@/pages/Home'));
const Logout = lazy(() => import('@/pages/Logout'));
const Lookup = lazy(() => import('@/pages/Lookup'));
const Markets = lazy(() => import('@/pages/Markets'));
const Login = lazy(() => import('@/pages/Onboarding/features/Login'));
const OnboardingForm = lazy(() => import('@/pages/Onboarding/OnboardingForm'));
const Profile = lazy(() => import('@/pages/Profile'));
const ManageSubscription = lazy(() => import('@/pages/Subscription'));
const SubscriptionSuccess = lazy(
  () => import('@/pages/Subscription/components/SubscriptionSuccess')
);
const FileDownload = lazy(() => import('./pages/FileDownload'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Welcome = lazy(() => import('./pages/Welcome'));

const MaintenanceGate = () => {
  const isMaintenanceMode = useFeatureFlagEnabled(MAINTENANCE_MODE_FLAG);
  if (isMaintenanceMode) {
    return <MaintenanceMode />;
  }

  return <Outlet />; // normal app when not in maintenance mode
};

const Wrapper = () => {
  const [toggleErrorBoundary, setToggleErrorBoundary] = useState(false);

  const content = (
    <>
      <Listeners />
      <GlobalModal />
      <CookieBanner />
      <WidgetAnimationProvider>
        <TrackingLister>
          <MaintenanceGate />
        </TrackingLister>
      </WidgetAnimationProvider>
    </>
  );

  return (
    <IdentifyPostHogProvider>
      {import.meta.env.DEV ? (
        content
      ) : (
        <PostHogErrorBoundary
          key={toggleErrorBoundary.toString()} // Used to force remount when toogleErrorBoundary changes
          fallback={<ErrorPage toggleErrorBoundary={setToggleErrorBoundary} />}
        >
          {content}
        </PostHogErrorBoundary>
      )}
    </IdentifyPostHogProvider>
  );
};

const createAppRoutes = () =>
  createRoutesFromElements(
    <Route element={<Wrapper />}>
      <Route
        element={withSuspense(<Onboarding />)}
        path='/onboarding'
      />
      <Route
        element={withSuspense(<Login />)}
        path='/login'
      />
      <Route
        element={withSuspense(<OnboardingForm />)}
        path='/onboarding/form'
      />
      <Route
        element={withSuspense(<Logout />)}
        path='/logout'
      />
      <Route
        element={withSuspense(<Expired />)}
        path='/expired'
      />

      <Route element={<PublicLayout />}>
        <Route
          index
          element={withSuspense(<Home />)}
        />
        <Route
          element={withSuspense(<Home />)}
          path='/token'
        />
        <Route
          element={withSuspense(<Lookup />)}
          path={`/:${FACTSHEET_PARAM_NAME}`}
        />
        <Route element={<AuthLayout />}>
          <Route
            element={withSuspense(<Welcome />)}
            path='/welcome'
          />
          <Route
            element={withSuspense(<FileDownload />)}
            path='/file/:fileName/:fileId'
          />
          <Route element={<AppLayout />}>
            <Route
              element={withSuspense(<Markets />, 'markets')}
              path='markets/:marketId?/:tabId?'
            />
            <Route
              element={withSuspense(<Catalog />)}
              path={`/catalog/${SPOT_CATALOG_PATH}`}
            />
            <Route
              element={withSuspense(<Catalog />)}
              path={`/catalog/${FWDCURVE_CATALOG_PATH}`}
            />
            <Route
              element={withSuspense(<Profile />)}
              path='/profile'
            />
            <Route
              element={withSuspense(<ManageSubscription />)}
              path='/manage-subscription'
            />
            <Route
              element={<PersonalizationVariantRenderer componentName='dashboard_route_element' />}
              path='/dashboard/:dashboardId?/:tabName?'
            />
          </Route>
          <Route
            element={withSuspense(<SubscriptionSuccess />)}
            path='/subscription/success'
          />
        </Route>
      </Route>
      <Route
        element={withSuspense(<ErrorPage />)}
        path='/notfound'
      />
      <Route
        element={withSuspense(<ErrorPage />)}
        path='/:section/*'
      />
      <Route
        element={withSuspense(<ErrorPage />)}
        path='*'
      />
      {import.meta.env.DEV && (
        <Route
          element={withSuspense(<DataHubExport />)}
          path='test-page'
        />
      )}
    </Route>
  );

export const createAppMemoryRouter = (options?: Parameters<typeof createMemoryRouter>[1]) =>
  createMemoryRouter(createAppRoutes(), options);

export const router = createBrowserRouter(createAppRoutes());
